/**
 * Feature flags (GDD §15: purchases and a season pass stay behind one; voice chat is reserved). The
 * table `feature_flags` says which are on and for what share of the players; what the app does with
 * no answer (no account, no network, a flag nobody has written down) is `FLAG_DEFAULTS`. Pure, so the
 * server and the client decide the same way.
 */

export const FLAGS = [
  'ranked',
  'daily',
  'friends',
  'replays',
  'news',
  'voice_chat',
  'purchases',
] as const;
export type FlagKey = (typeof FLAGS)[number];

/** What a flag is when the service has nothing to say about it: the features that exist are on, the reserved ones off. */
export const FLAG_DEFAULTS: Record<FlagKey, boolean> = {
  ranked: true,
  daily: true,
  friends: true,
  replays: true,
  news: true,
  voice_chat: false,
  purchases: false,
};

export interface FlagRow {
  key: string;
  enabled: boolean;
  /** 0–100: the share of the players who have it, when it is enabled. */
  rolloutPercent: number;
}

/** A fixed place from 0 to 99 for a player on a flag (FNV-1a of `flag:account`): the same every time. */
export function rolloutBucket(key: string, userId: string): number {
  const text = `${key}:${userId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

/**
 * Whether a flag is on for a player. `rows` is what the service said (null: nothing yet).
 * A flag that is on for a share of the players is off for somebody who is not signed in.
 */
export function flagOn(
  rows: readonly FlagRow[] | null,
  key: FlagKey,
  userId: string | null,
): boolean {
  const row = rows?.find((r) => r.key === key);
  if (!row) return FLAG_DEFAULTS[key];
  if (!row.enabled) return false;
  if (row.rolloutPercent >= 100) return true;
  if (row.rolloutPercent <= 0 || userId === null) return false;
  return rolloutBucket(key, userId) < row.rolloutPercent;
}
